/**
 * Waschschleuderautomaten (WSA) — Auswertung im Leitstand-Stil (Phase B, Z3).
 *
 * Der Kernunterschied zur klassischen Ansicht: „Top Kunden" enthält nur echte
 * Kunden; Sammelwäsche (Bewohnerwäsche, Sammelposten, Mietwäsche …) steht als
 * eigener Block daneben statt zwischen den Kunden. Kompakte Anteilslisten
 * statt raumgreifender Balkendiagramme.
 *
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTodayBerlin } from '@wash-ops/shared';
import { useAuth } from '../App';
import { LeitstandRahmen } from '../components/leitstand/LeitstandRahmen';
import { Anteilsliste, Karte, Kennzahl, Notiz } from '../components/leitstand';

interface Zeile {
  name: string;
  kg: number;
  anzahl: number;
}

interface LeitstandDaten {
  gesamt: {
    kg: number;
    hbwKg: number;
    erfassungen: number;
    korrigierte: number;
    veraenderungProzent: number | null;
  };
  topKunden: Zeile[];
  sammelwaesche: Zeile[];
  maschinen: Zeile[];
  programme: Zeile[];
}

type Preset = 'heute' | 'woche' | 'monat' | 'quartal' | 'frei';

function tagPlus(iso: string, delta: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

function zeitraumFuer(preset: Preset): { von: string; bis: string } {
  const heute = getTodayBerlin(new Date());
  if (preset === 'heute') return { von: heute, bis: heute };
  if (preset === 'woche') {
    const wochentag = new Date(`${heute}T00:00:00Z`).getUTCDay();
    return { von: tagPlus(heute, -((wochentag + 6) % 7)), bis: heute };
  }
  if (preset === 'monat') return { von: `${heute.slice(0, 8)}01`, bis: heute };
  return { von: tagPlus(heute, -89), bis: heute };
}

const PRESETS: Array<{ schluessel: Preset; label: string }> = [
  { schluessel: 'heute', label: 'Heute' },
  { schluessel: 'woche', label: 'Diese Woche' },
  { schluessel: 'monat', label: 'Dieser Monat' },
  { schluessel: 'quartal', label: '90 Tage' },
  { schluessel: 'frei', label: 'Zeitraum' },
];

function fmt(wert: number, nachkomma = 0): string {
  return wert.toLocaleString('de-DE', {
    minimumFractionDigits: nachkomma,
    maximumFractionDigits: nachkomma,
  });
}

function alsAnteile(zeilen: Zeile[], max = 10) {
  const top = zeilen.slice(0, max);
  const spitze = top[0]?.kg ?? 0;
  const rest = zeilen.slice(max);
  const eintraege = top.map((z) => ({
    name: z.name,
    wert: `${fmt(z.kg)} kg`,
    anteil: spitze > 0 ? Math.max(2, (z.kg / spitze) * 100) : 0,
  }));
  if (rest.length > 0) {
    const restKg = rest.reduce((s, z) => s + z.kg, 0);
    eintraege.push({
      name: `übrige (${rest.length})`,
      wert: `${fmt(restKg)} kg`,
      anteil: spitze > 0 ? Math.max(2, (restKg / spitze) * 100) : 0,
    });
  }
  return eintraege;
}

export default function WsaLeitstand() {
  const { user } = useAuth();

  const [preset, setPreset] = useState<Preset>('woche');
  const [freiVon, setFreiVon] = useState('');
  const [freiBis, setFreiBis] = useState('');
  const [daten, setDaten] = useState<LeitstandDaten | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');

  const zeitraum = useMemo(() => {
    if (preset === 'frei') return freiVon && freiBis ? { von: freiVon, bis: freiBis } : null;
    return zeitraumFuer(preset);
  }, [preset, freiVon, freiBis]);

  const lade = useCallback(async () => {
    if (!zeitraum) return;
    setLaden(true);
    setFehler('');
    try {
      const res = await fetch(
        `/api/reports/wsa/leitstand?from=${zeitraum.von}&to=${zeitraum.bis}`,
        { credentials: 'include' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Die Auswertung konnte nicht geladen werden');
      }
      const body = await res.json();
      if (!body?.gesamt || !Array.isArray(body.topKunden)) {
        throw new Error('Unerwartete Antwort vom Server');
      }
      setDaten(body);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Auswertung konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, [zeitraum]);

  useEffect(() => {
    void lade();
  }, [lade]);

  const sammelKg = daten ? daten.sammelwaesche.reduce((s, z) => s + z.kg, 0) : 0;

  return (
    <LeitstandRahmen
      titel="Waschschleuderautomaten"
      benutzer={user?.displayName}
      werkzeuge={
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.schluessel}
                onClick={() => setPreset(p.schluessel)}
                aria-pressed={preset === p.schluessel}
                className={`rounded-[4px] border px-[11px] py-1.5 font-zahl text-xs transition-colors ${
                  preset === p.schluessel
                    ? 'border-akzent bg-akzent text-akzent-satz'
                    : 'border-linie-2 bg-flaeche text-tinte-2 hover:border-linie-stark'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {preset === 'frei' && (
            <span className="flex items-center gap-1.5">
              <input
                type="date"
                value={freiVon}
                onChange={(e) => setFreiVon(e.target.value)}
                className="rounded-[4px] border border-linie-2 bg-flaeche px-2 py-1 font-zahl text-xs text-tinte"
              />
              <span className="text-xs text-tinte-3">bis</span>
              <input
                type="date"
                value={freiBis}
                onChange={(e) => setFreiBis(e.target.value)}
                className="rounded-[4px] border border-linie-2 bg-flaeche px-2 py-1 font-zahl text-xs text-tinte"
              />
            </span>
          )}
        </div>
      }
    >
      {fehler && (
        <Notiz titel="Konnte nicht laden">
          {fehler}{' '}
          <button onClick={() => void lade()} className="underline">
            Erneut versuchen
          </button>
        </Notiz>
      )}

      {laden && !daten && (
        <p className="py-12 text-center text-sm text-tinte-3">Lade Auswertung …</p>
      )}

      {daten && (
        <div className={`flex flex-col gap-6 ${laden ? 'opacity-60' : ''}`}>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kennzahl
              marke="Gewaschen"
              wert={fmt(daten.gesamt.kg / 1000, 2)}
              einheit="t"
              neben={
                daten.gesamt.veraenderungProzent === null
                  ? undefined
                  : `${daten.gesamt.veraenderungProzent > 0 ? '+' : ''}${fmt(daten.gesamt.veraenderungProzent, 1)} % zum Zeitraum davor`
              }
              tendenz={
                daten.gesamt.veraenderungProzent === null
                  ? 'neutral'
                  : daten.gesamt.veraenderungProzent >= 0
                    ? 'hoch'
                    : 'runter'
              }
            />
            <Kennzahl
              marke="davon Bewohnerwäsche"
              wert={fmt(daten.gesamt.hbwKg / 1000, 2)}
              einheit="t"
            />
            <Kennzahl
              marke="Erfassungen"
              wert={fmt(daten.gesamt.erfassungen)}
              neben={
                daten.gesamt.korrigierte > 0
                  ? `${fmt(daten.gesamt.korrigierte)} korrigiert`
                  : undefined
              }
            />
            <Kennzahl
              marke="Sammelwäsche"
              wert={fmt(sammelKg / 1000, 2)}
              einheit="t"
              neben="ohne festen Kunden"
            />
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <Karte titel="Top Kunden" marke="nur echte Kunden · nach Gewicht">
              {daten.topKunden.length === 0 ? (
                <p className="py-4 text-sm text-tinte-3">Keine Erfassungen im Zeitraum.</p>
              ) : (
                <Anteilsliste eintraege={alsAnteile(daten.topKunden)} />
              )}
            </Karte>

            <Karte titel="Sammelwäsche" marke="Sammelposten und Gruppen · getrennt von den Kunden">
              {daten.sammelwaesche.length === 0 ? (
                <p className="py-4 text-sm text-tinte-3">Keine Sammelwäsche im Zeitraum.</p>
              ) : (
                <Anteilsliste eintraege={alsAnteile(daten.sammelwaesche)} />
              )}
            </Karte>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <Karte titel="Maschinen" marke="nach Gewicht">
              {daten.maschinen.length === 0 ? (
                <p className="py-4 text-sm text-tinte-3">Keine Daten.</p>
              ) : (
                <Anteilsliste eintraege={alsAnteile(daten.maschinen)} />
              )}
            </Karte>

            <Karte titel="Programme" marke="nach Gewicht">
              {daten.programme.length === 0 ? (
                <p className="py-4 text-sm text-tinte-3">Keine Daten.</p>
              ) : (
                <Anteilsliste eintraege={alsAnteile(daten.programme, 8)} />
              )}
            </Karte>
          </div>

          <p className="border-t border-linie pt-4 text-xs text-tinte-3">
            Alle Werte sind wirksame Werte — angenommene Korrekturen zählen.
          </p>
        </div>
      )}
    </LeitstandRahmen>
  );
}
